import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import clsx from 'clsx';

interface Campaign {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  category: string;
  visibility: string;
  featured: boolean;
  trackingUrl: string;
  cookieLifetimeDays: number;
  currency: string;
  _count?: {
    goals: number;
    campaignAffiliates: number;
  };
  // My status in this campaign
  myStatus?: 'pending' | 'approved' | 'rejected' | 'suspended' | null;
}

const categoryLabels: Record<string, string> = {
  casino: 'Casino',
  sports: 'Sports Betting',
  poker: 'Poker',
  lottery: 'Lottery',
  forex: 'Forex',
  crypto: 'Crypto',
};

export default function Campaigns() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const category = searchParams.get('category') || '';
  const view = searchParams.get('view') || 'marketplace';

  // Marketplace campaigns (public + ones we can apply to)
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns', { category, search, view }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      params.set('view', view);

      const response = await api.get(`/campaigns/affiliate/browse?${params}`);
      return response.data;
    },
  });

  const applyToCampaign = useMutation({
    mutationFn: async (campaignId: string) => {
      await api.post(`/campaigns/${campaignId}/apply`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Application submitted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to apply');
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
            {view === 'my' ? 'My Campaigns' : 'Campaign Marketplace'}
          </h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {view === 'my'
              ? 'Campaigns you have joined or applied to.'
              : 'Browse available campaigns and apply to promote them.'}
          </p>
        </div>
      </div>

      {/* View Tabs */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            params.delete('view');
            setSearchParams(params);
          }}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            view === 'marketplace' || !view
              ? 'bg-primary-100 text-primary-700'
              : 'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
          )}
        >
          Marketplace
        </button>
        <button
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            params.set('view', 'my');
            setSearchParams(params);
          }}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            view === 'my'
              ? 'bg-primary-100 text-primary-700'
              : 'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
          )}
        >
          My Campaigns
        </button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-xs"
        />
        <select
          value={category}
          onChange={(e) => {
            const params = new URLSearchParams(searchParams);
            if (e.target.value) params.set('category', e.target.value);
            else params.delete('category');
            setSearchParams(params);
          }}
          className="input max-w-xs"
        >
          <option value="">All Categories</option>
          {Object.entries(categoryLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Campaign Grid */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full text-center py-10 text-[var(--text-muted)]">Loading...</div>
        ) : campaigns?.data?.length === 0 ? (
          <div className="col-span-full text-center py-10 text-[var(--text-muted)]">
            {view === 'my' ? 'You haven\'t joined any campaigns yet.' : 'No campaigns found.'}
          </div>
        ) : (
          campaigns?.data?.map((campaign: Campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onApply={() => applyToCampaign.mutate(campaign.id)}
              isApplying={applyToCampaign.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

function CampaignCard({
  campaign,
  onApply,
  isApplying,
}: {
  campaign: Campaign;
  onApply: () => void;
  isApplying: boolean;
}) {
  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    suspended: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
  };

  return (
    <div className="bg-[var(--bg-secondary)] rounded-lg shadow overflow-hidden">
      {/* Header with logo */}
      <div className="h-32 bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        {campaign.logoUrl ? (
          <img
            src={campaign.logoUrl}
            alt={campaign.name}
            className="h-20 max-w-[80%] object-contain"
          />
        ) : (
          <span className="text-4xl font-bold text-white">
            {campaign.name.charAt(0)}
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-[var(--text-primary)]">{campaign.name}</h3>
            <p className="text-sm text-[var(--text-muted)]">
              {categoryLabels[campaign.category] || campaign.category}
            </p>
          </div>
          {campaign.featured && (
            <span className="badge bg-amber-100 text-amber-800">Featured</span>
          )}
        </div>

        {campaign.description && (
          <p className="mt-2 text-sm text-[var(--text-secondary)] line-clamp-2">
            {campaign.description}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <div className="text-[var(--text-muted)]">
            Cookie: <span className="text-[var(--text-primary)]">{campaign.cookieLifetimeDays}d</span>
          </div>
          <div className="text-[var(--text-muted)]">
            Currency: <span className="text-[var(--text-primary)]">{campaign.currency}</span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          {campaign.myStatus ? (
            <>
              <span className={clsx('badge', statusColors[campaign.myStatus])}>
                {campaign.myStatus === 'approved' ? 'Joined' : campaign.myStatus}
              </span>
              {campaign.myStatus === 'approved' && (
                <Link
                  to={`/campaigns/${campaign.id}`}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View Details &rarr;
                </Link>
              )}
            </>
          ) : (
            <button
              onClick={onApply}
              disabled={isApplying}
              className="btn btn-primary btn-sm w-full"
            >
              {isApplying ? 'Applying...' : 'Apply Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
